import { SignIn, SignUp } from "@clerk/clerk-react";
import { KeyRound, LogIn } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSession, signInWithPasscode } from "../api";
import { useAuthMode } from "../components/ConfigProvider";
import { PoweredByStatic } from "../components/PoweredBy";
import { translate, loadLocale, storeLocale, type Locale } from "../i18n";
import { applyBrand } from "../theme";

/**
 * Sign-in page. Three deployments, one screen:
 *   dev      → no sign-in; a button into the workspace.
 *   passcode → name, email and the private access code (demo tomorrow).
 *   clerk    → Clerk's component embedded in this page. `/sign-up` is the same page with Clerk's sign-up component: the
 *               instance is invite-only, so it only completes with the ticket carried by an invitation link. Both keep
 *               the person on the agency's own domain; Clerk's hosted account portal is never linked.
 */
export function SignInScreen({ variant = "sign-in" }: { variant?: "sign-in" | "sign-up" }) {
  const auth = useAuthMode();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [locale, setLocale] = useState<Locale>(() => loadLocale(auth.publicAgency?.defaultLocale ?? "en"));
  const t = (k: string) => translate(locale, k);
  const agency = auth.publicAgency;

  useEffect(() => {
    if (agency) {
      applyBrand(agency.brand);
      document.title = `${agency.displayName} · ${t("signin.title")}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency, locale]);

  // Already holding a valid passcode session → straight in.
  useEffect(() => {
    if (auth.mode === "passcode" && getSession()) navigate(next, { replace: true });
  }, [auth.mode, navigate, next]);

  return (
    <div className="min-h-screen bg-surface text-ink font-body flex flex-col">
      <header className="bg-ink text-white">
        <div className="mx-auto flex max-w-md items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            {agency ? <img src={agency.brand.logoPath} alt={agency.displayName} className="h-10 w-auto rounded bg-white p-1" /> : null}
            <div>
              <span className="heading text-lg font-bold block leading-tight">{agency?.displayName ?? ""}</span>
              <PoweredByStatic locale={locale} tone="onDark" />
            </div>
          </div>
          <div className="flex rounded-xl border border-white/30 overflow-hidden" role="group" aria-label={t("signin.language")}>
            {(agency?.supportedLocales ?? ["en", "es"]).map((l) => (
              <button key={l} onClick={() => { setLocale(l); storeLocale(l); }} aria-pressed={locale === l} className={`min-h-[44px] px-4 text-sm font-bold ${locale === l ? "bg-white text-ink" : "text-white/80"}`}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-md px-4 py-8 flex-1">
        {auth.mode === "dev" ? (
          <section className="rounded-3xl bg-white p-6 shadow-card space-y-4">
            <h1 className="text-2xl font-bold">{t("signin.title")}</h1>
            <p className="text-ink/70">{t("signin.dev")}</p>
            <button onClick={() => navigate(next, { replace: true })} className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-primary px-6 text-lg font-bold text-white hover:bg-primary-dark">
              <LogIn className="h-5 w-5" /> {t("signin.devGo")}
            </button>
          </section>
        ) : auth.mode === "clerk" ? (
          auth.clerkPublishableKey ? (
            <div className="flex justify-center">
              {variant === "sign-up" ? (
                <SignUp routing="path" path="/sign-up" fallbackRedirectUrl={next} signInUrl="/sign-in" />
              ) : (
                <SignIn routing="path" path="/sign-in" fallbackRedirectUrl={next} signUpUrl="/sign-up" />
              )}
            </div>
          ) : (
            <section className="rounded-3xl bg-white p-6 shadow-card"><p className="text-red-700">{t("signin.clerkMissing")}</p></section>
          )
        ) : (
          <PasscodeForm locale={locale} next={next} />
        )}
      </main>
      <footer className="px-4 pb-6 text-center text-xs text-ink/50">{t("signin.private")}</footer>
    </div>
  );
}

function PasscodeForm({ locale, next }: { locale: Locale; next: string }) {
  const t = (k: string) => translate(locale, k);
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", passcode: "" });
  const [state, setState] = useState<"idle" | "working" | "error">("idle");
  const [error, setError] = useState("");
  const field = "w-full min-h-[52px] rounded-2xl border border-ink/15 bg-white px-4 text-lg text-ink placeholder:text-ink/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (state === "working") return;
    setState("working");
    setError("");
    const r = await signInWithPasscode({ name: form.name.trim(), email: form.email.trim(), passcode: form.passcode.trim() });
    if (r.ok) {
      navigate(next, { replace: true });
      return;
    }
    setState("error");
    setError(r.status === 401 ? t("signin.error.code") : r.status === 403 ? r.error || t("signin.error.inactive") : r.error || t("signin.error.generic"));
  };

  return (
    <section className="rounded-3xl bg-white p-6 shadow-card">
      <div className="mb-4 flex items-center gap-3">
        <span className="h-11 w-11 rounded-2xl bg-primary/15 text-primary-dark flex items-center justify-center"><KeyRound className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-bold">{t("signin.title")}</h1>
          <p className="text-sm text-ink/60">{t("signin.sessionKept")}</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-ink/80">{t("signin.name")}</span>
          <input className={field} required autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-ink/80">{t("signin.email")}</span>
          <input className={field} required type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-ink/80">{t("signin.code")}</span>
          <input className={field} required type="password" autoComplete="one-time-code" value={form.passcode} onChange={(e) => setForm({ ...form, passcode: e.target.value })} />
          <span className="mt-1 block text-xs text-ink/55">{t("signin.codeHelp")}</span>
        </label>
        {state === "error" ? <p role="alert" className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">{error}</p> : null}
        <button type="submit" disabled={state === "working" || !form.name.trim() || !form.email.trim() || !form.passcode.trim()} className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-primary px-6 text-lg font-bold text-white hover:bg-primary-dark disabled:opacity-50">
          <LogIn className="h-5 w-5" /> {state === "working" ? t("signin.working") : t("signin.submit")}
        </button>
      </form>
    </section>
  );
}

/** Only same-origin app paths; never an absolute URL from the query string. */
function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/sign-in")) return "/";
  return raw;
}
